import { useEffect, useRef, useState } from "react";
import { Paperclip, X } from "@phosphor-icons/react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ROLES } from "@/lib/roles";
import {
  ATTACHMENT_ACCEPT,
  ATTACHMENT_MAX_BYTES,
  ATTACHMENT_MAX_PER_MESSAGE,
} from "@/lib/messaging";

/**
 * Role-aware composer for starting a new conversation.
 *
 *   teacher: [family, classroom-announcement]
 *   admin:   [family, classroom-announcement, school-announcement]
 *   parent:  — (not rendered; parents reply only in Phase 2.2)
 *
 * `families` / `classrooms` are already pre-filtered server-side per role.
 *
 * Module 45 · attachments — picks files locally and bubbles them up via
 * onSubmit. The page (Inbox) creates the conversation, gets the first
 * message back, and then calls uploadMessageAttachments with
 * messageId = first_message.id. This mirrors the thread-message
 * attachment pattern; we don't store anything in the compose dialog
 * beyond the in-memory File list.
 */
export default function ComposeDialog({
  open,
  onOpenChange,
  role,
  families = [],
  classrooms = [],
  onSubmit,
  restoreFocusRef,
}) {
  const isAdmin = role === ROLES.ADMIN;
  const typeChoices = isAdmin
    ? ["family", "classroom-announcement", "school-announcement"]
    : ["family", "classroom-announcement"];

  const [type, setType] = useState(typeChoices[0]);
  const [familyId, setFamilyId] = useState(families[0]?.id || "");
  const [classroomId, setClassroomId] = useState(classrooms[0]?.id || "");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [attachments, setAttachments] = useState([]); // File[]
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setType(typeChoices[0]);
    setFamilyId(families[0]?.id || "");
    setClassroomId(classrooms[0]?.id || "");
    setSubject("");
    setBody("");
    setAttachments([]);
    setSubmitting(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const needsFamily = type === "family";
  const needsClassroom = type === "classroom-announcement";

  const canSubmit =
    !submitting &&
    body.trim().length > 0 &&
    (!needsFamily || familyId) &&
    (!needsClassroom || classroomId);

  const handleAddFiles = (incoming) => {
    const arr = Array.from(incoming || []);
    if (arr.length === 0) return;

    // Same per-message + per-file caps as thread messages.
    const merged = [...attachments];
    for (const f of arr) {
      if (merged.length >= ATTACHMENT_MAX_PER_MESSAGE) break;
      if (f.size > ATTACHMENT_MAX_BYTES) continue;
      // Cheap dedup by name+size.
      const dup = merged.some((m) => m.name === f.name && m.size === f.size);
      if (dup) continue;
      merged.push(f);
    }
    setAttachments(merged);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeAttachment = (idx) => {
    setAttachments((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    let payload;
    if (type === "family") {
      const fam = families.find((f) => f.id === familyId);
      payload = {
        type: "family",
        subject: subject || fam?.display_name || "Family thread",
        familyId,
        classroomId: null,
        participantIds: fam?.participantIds || [],
      };
    } else if (type === "classroom-announcement") {
      const cr = classrooms.find((c) => c.id === classroomId);
      payload = {
        type: "announcement",
        subject: subject || `${cr?.name || "Classroom"} announcement`,
        classroomId,
        familyId: null,
        participantIds: cr?.participantIds || [],
      };
    } else {
      payload = {
        type: "announcement",
        subject: subject || "School announcement",
        classroomId: null,
        familyId: null,
        participantIds: [],
      };
    }
    try {
      await onSubmit({
        ...payload,
        firstMessageBody: body.trim(),
        attachments,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="compose-dialog"
        className="max-w-lg rounded-2xl border-neutral-200 bg-white p-0"
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          restoreFocusRef?.current?.focus?.({ preventScroll: true });
        }}
      >
        <DialogHeader className="border-b border-neutral-100 px-6 pb-4 pt-5">
          <DialogTitle className="font-heading text-xl font-semibold tracking-tight text-neutral-900">
            New message
          </DialogTitle>
          <DialogDescription className="sr-only">
            Start a new conversation with a family or post an announcement.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5 px-6 py-5">
          {/* Type picker */}
          <div>
            <p className="mb-2 text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">
              Type
            </p>
            <div className="flex flex-wrap gap-2" data-testid="compose-type">
              {typeChoices.map((t) => {
                const selected = t === type;
                return (
                  <button
                    key={t}
                    type="button"
                    data-testid={`compose-type-${t}`}
                    onClick={() => setType(t)}
                    className={`rounded-full border px-4 py-2 text-sm font-medium transition-all ${
                      selected
                        ? "border-primary bg-primary/10 text-primary-hover"
                        : "border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-50"
                    }`}
                  >
                    {labelForType(t)}
                  </button>
                );
              })}
            </div>
          </div>

          {needsFamily && (
            <Select
              testId="compose-family"
              label="Family"
              value={familyId}
              onChange={setFamilyId}
              options={families.map((f) => ({
                value: f.id,
                label: f.display_name,
              }))}
            />
          )}

          {needsClassroom && (
            <Select
              testId="compose-classroom"
              label="Classroom"
              value={classroomId}
              onChange={setClassroomId}
              options={classrooms.map((c) => ({ value: c.id, label: c.name }))}
            />
          )}

          <TextField
            testId="compose-subject"
            label="Subject (optional)"
            value={subject}
            onChange={setSubject}
            placeholder={defaultSubjectPlaceholder(type)}
          />

          <TextArea
            testId="compose-body"
            label="Message"
            value={body}
            onChange={setBody}
            rows={5}
            placeholder="Write your message…"
          />

          {/* Attachments — same per-message + per-file caps as the
              thread reply composer. We keep the UI compact (one paperclip
              row + a chip stack) so the modal doesn't feel crowded. */}
          <div data-testid="compose-attachments-section">
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">
                Attachments {attachments.length > 0 && `(${attachments.length}/${ATTACHMENT_MAX_PER_MESSAGE})`}
              </span>
              <button
                type="button"
                data-testid="compose-attachment-trigger"
                disabled={attachments.length >= ATTACHMENT_MAX_PER_MESSAGE}
                onClick={() => fileInputRef.current?.click()}
                className="inline-flex items-center gap-1.5 rounded-full border border-neutral-200 bg-white px-3 py-1.5 text-xs font-semibold text-neutral-700 transition hover:bg-neutral-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Paperclip size={13} weight="duotone" />
                Add files
              </button>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                hidden
                accept={ATTACHMENT_ACCEPT.join(",")}
                onChange={(e) => handleAddFiles(e.target.files)}
                data-testid="compose-attachment-input"
              />
            </div>
            {attachments.length > 0 && (
              <ul
                data-testid="compose-attachment-list"
                className="mt-2 flex flex-wrap gap-1.5"
              >
                {attachments.map((f, i) => (
                  <li
                    key={`${f.name}:${f.size}:${i}`}
                    data-testid={`compose-attachment-chip-${i}`}
                    className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-neutral-200 bg-neutral-50 py-1 pl-3 pr-1 text-xs text-neutral-700"
                  >
                    <span className="truncate max-w-[180px]" title={f.name}>{f.name}</span>
                    <span className="text-[10px] text-neutral-400">
                      {(f.size / 1024).toFixed(0)} KB
                    </span>
                    <button
                      type="button"
                      data-testid={`compose-attachment-remove-${i}`}
                      aria-label={`Remove ${f.name}`}
                      onClick={() => removeAttachment(i)}
                      className="ml-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-white text-neutral-500 hover:bg-neutral-200 hover:text-neutral-800"
                    >
                      <X size={10} weight="bold" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {attachments.length === 0 && (
              <p className="mt-1 text-[11px] text-neutral-400">
                Up to {ATTACHMENT_MAX_PER_MESSAGE} files · 25 MB each. Photos, PDFs, videos, text.
              </p>
            )}
          </div>

          <div className="flex items-center justify-end gap-3 pt-1">
            <button
              type="button"
              data-testid="compose-cancel"
              onClick={() => onOpenChange(false)}
              className="rounded-full px-5 py-2.5 text-sm font-medium text-neutral-600 hover:bg-neutral-100"
            >
              Cancel
            </button>
            <button
              type="submit"
              data-testid="compose-submit"
              disabled={!canSubmit}
              className="rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-primary-hover active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50"
            >
              {submitting
                ? attachments.length > 0
                  ? "Sending & uploading…"
                  : "Sending…"
                : "Send"}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function labelForType(t) {
  switch (t) {
    case "family":
      return "Family";
    case "classroom-announcement":
      return "Classroom announcement";
    case "school-announcement":
      return "School announcement";
    default:
      return t;
  }
}
function defaultSubjectPlaceholder(t) {
  if (t === "school-announcement") return "e.g. Fall enrollment now open";
  if (t === "classroom-announcement") return "e.g. Library visit tomorrow";
  return "e.g. About Noa's lunch";
}

function Select({ label, value, onChange, options, testId }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">
        {label}
      </span>
      <select
        data-testid={testId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-xl border border-neutral-200 bg-white px-3 py-2.5 text-sm text-neutral-900 shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}
function TextField({ label, value, onChange, placeholder, testId }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">
        {label}
      </span>
      <input
        data-testid={testId}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full rounded-xl border border-neutral-200 bg-white px-4 py-2.5 text-sm text-neutral-900 shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
      />
    </label>
  );
}
function TextArea({ label, value, onChange, placeholder, testId, rows }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">
        {label}
      </span>
      <textarea
        data-testid={testId}
        value={value}
        rows={rows || 3}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full rounded-xl border border-neutral-200 bg-white px-4 py-2.5 text-sm text-neutral-900 shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
      />
    </label>
  );
}
